/**
 * Compatibility composable to ease migration from Vuex to Pinia
 * This provides a Vuex-like API using Pinia stores
 */
// @ts-ignore
import { computed } from 'vue'
import { useAuthStore, useFirmStore, useProjectStore, useUiStore } from '@/stores'

export function useStore() {
  const authStore = useAuthStore()
  const firmStore = useFirmStore()
  const projectStore = useProjectStore()
  const uiStore = useUiStore()

  // Vuex-like getters with additional safety
  const getters = {
    // Auth getters
    isAuthenticated: computed(() => {
      try {
        return authStore?.isAuthenticated?.value ?? false
      } catch (error) {
        console.warn('Error accessing isAuthenticated:', error)
        return false
      }
    }),
    getUser: computed(() => {
      try {
        // authStore.user is auto-unwrapped by Pinia, so it's the actual user value
        return authStore?.user ?? null
      } catch (error) {
        return null
      }
    }),
    getUserCurrency: computed(() => {
      try {
        return authStore?.getUserCurrency?.value ?? 'USD'
      } catch (error) {
        return 'USD'
      }
    }),
    getAccessToken: computed(() => {
      try {
        return authStore?.getAccessToken?.value ?? null
      } catch (error) {
        return null
      }
    }),
    getRefreshToken: computed(() => {
      try {
        return authStore?.getRefreshToken?.value ?? null
      } catch (error) {
        return null
      }
    }),
    
    // Firm getters
    getSelectedFirm: computed(() => {
      try {
        return firmStore?.getSelectedFirm?.value ?? null
      } catch (error) {
        return null
      }
    }),
    
    // Project getters
    getCurrentProject: computed(() => {
      try {
        return projectStore?.getCurrentProject?.value ?? null
      } catch (error) {
        return null
      }
    }),
    getCurrentProjectName: computed(() => {
      try {
        return projectStore?.getCurrentProjectName?.value ?? null
      } catch (error) {
        return null
      }
    }),
    
    // UI getters
    isLoading: computed(() => {
      try {
        return uiStore?.isLoading?.value ?? false
      } catch (error) {
        return false
      }
    }),
    getError: computed(() => {
      try {
        return uiStore?.getError?.value ?? null
      } catch (error) {
        return null
      }
    }),
    getSuccess: computed(() => {
      try {
        return uiStore?.getSuccess?.value ?? null
      } catch (error) {
        return null
      }
    }),
    getModal: computed(() => {
      try {
        return uiStore?.getModal?.value ?? null
      } catch (error) {
        return null
      }
    }),
    
    // Locale getter with safe access
    getCurrentLocale: computed(() => {
      // Return default locale since we removed i18n dependency
      return 'en-US'
    }),
  }

  // Vuex-like commit function
  function commit(mutation: string, payload?: any) {
    switch (mutation) {
      // Auth mutations
      case 'SET_USER':
        authStore.setUser(payload)
        break
      case 'SET_AUTHENTICATED':
        // This is handled automatically in setUser
        break
      case 'SET_ACCESS_TOKEN':
        authStore.setAccessToken(payload)
        break
      case 'SET_REFRESH_TOKEN':
        authStore.setRefreshToken(payload)
        break
      case 'SET_AUTH_CHECKED':
        authStore.setAuthChecked(payload)
        break
      case 'CLEAR_AUTH':
        authStore.clearAuth()
        break
        
      // Firm mutations
      case 'SET_SELECTED_FIRM':
        firmStore.selectFirm(payload)
        break
        
      // Project mutations
      case 'SET_CURRENT_PROJECT':
        projectStore.setCurrentProject(payload)
        break
      case 'CLEAR_CURRENT_PROJECT':
        projectStore.clearCurrentProject()
        break
        
      // UI mutations
      case 'SET_LOADING':
        uiStore.setLoading(payload)
        break
      case 'SET_ERROR':
        uiStore.setError(payload)
        break
      case 'SET_SUCCESS':
        uiStore.setSuccess(payload)
        break
      case 'CLEAR_MESSAGES':
        uiStore.clearMessages()
        break
      case 'SET_MODAL':
        uiStore.setModal(payload)
        break
      case 'SET_MESSAGES':
        uiStore.setMessages(payload)
        break
        
      default:
        console.warn(`Unknown mutation: ${mutation}`)
    }
  }

  // Vuex-like dispatch function
  async function dispatch(action: string, payload?: any) {
    switch (action) {
      // Auth actions
      case 'setUser':
        return await authStore.setUser(payload)
      case 'logout':
        return await authStore.logout()
      case 'initializeAuth':
        return await authStore.initializeAuth()
      case 'refreshTokenIfNeeded':
        return await authStore.refreshTokenIfNeeded()
      case 'refreshToken':
        return await authStore.performTokenRefresh()
      case 'authenticate':
        return await authStore.authenticate(payload)
      case 'checkAuth':
        return await authStore.checkAuth()
        
      // Firm actions
      case 'selectFirm':
        firmStore.selectFirm(payload)
        break
      case 'loadUserFirm':
        return await firmStore.loadUserFirm()
        
      // Project actions
      case 'setCurrentProject':
        projectStore.setCurrentProject(payload)
        break
      case 'clearCurrentProject':
        projectStore.clearCurrentProject()
        break
        
      // UI actions
      case 'setLoading':
        uiStore.setLoading(payload)
        break
      case 'setError':
        uiStore.setError(payload)
        break
      case 'setSuccess':
        uiStore.setSuccess(payload)
        break
      case 'clearMessages':
        uiStore.clearMessages()
        break
      case 'setModal':
        uiStore.setModal(payload)
        break
        
      default:
        console.warn(`Unknown action: ${action}`)
        return false
    }
  }

  return {
    getters,
    commit,
    dispatch
  }
}